import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from '@/stores/toastStore';
import {
  HiXMark,
  HiUserPlus,
  HiMagnifyingGlass,
  HiCheck,
  HiXCircle,
  HiTrash,
  HiUsers,
  HiBell,
  HiShieldCheck,
} from 'react-icons/hi2';
import { useAuthStore } from '@/stores/authStore';
import { ClansPanel } from './ClansPanel';
import './FriendsModal.css';

type Tab = 'friends' | 'pending' | 'add' | 'clans';

interface Friend {
  id: string;
  friend: {
    id: string;
    username: string;
    display_name: string;
    odyssey_title: string;
    odyssey_level: number;
    is_online: boolean;
  };
  created_at: number;
}

interface PendingRequest {
  id: string;
  friend: {
    id: string;
    username: string;
    display_name: string;
    odyssey_title: string;
  };
  created_at: number;
}

interface SentRequest {
  id: string;
  friend: {
    id: string;
    username: string;
    display_name: string;
    odyssey_title: string;
  };
  created_at: number;
}

interface SearchUser {
  id: string;
  username: string;
  display_name: string;
  odyssey_title: string;
  odyssey_level: number;
  is_friend: boolean;
  has_pending_request: boolean;
}

interface FriendsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FriendsModal({ isOpen, onClose }: FriendsModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('friends');
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<SentRequest[]>([]);
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const { token } = useAuthStore();

  // Load friends
  const loadFriends = async () => {
    if (!token) return;

    try {
      const response = await fetch('https://mkey-api-production.fidelismadboat.workers.dev/friendships?status=accepted', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setFriends(data.friendships || []);
      }
    } catch (error) {
      console.error('[FriendsModal] Error loading friends:', error);
    }
  };

  // Load pending requests (received)
  const loadPendingRequests = async () => {
    if (!token) return;

    try {
      const response = await fetch('https://mkey-api-production.fidelismadboat.workers.dev/friendships?status=pending&direction=received', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data.friendships || []);
      }
    } catch (error) {
      console.error('[FriendsModal] Error loading pending requests:', error);
    }
  };

  // Load sent requests (waiting for response)
  const loadSentRequests = async () => {
    if (!token) return;

    try {
      const response = await fetch('https://mkey-api-production.fidelismadboat.workers.dev/friendships?status=pending&direction=sent', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setSentRequests(data.friendships || []);
      }
    } catch (error) {
      console.error('[FriendsModal] Error loading sent requests:', error);
    }
  };

  // Search non-friends
  const searchUsers = async (query: string) => {
    if (!token || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`https://mkey-api-production.fidelismadboat.workers.dev/users/search-non-friends?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.users || []);
      }
    } catch (error) {
      console.error('[FriendsModal] Error searching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Send friend request
  const sendFriendRequest = async (friendId: string) => {
    if (!token) return;

    try {
      const response = await fetch('https://mkey-api-production.fidelismadboat.workers.dev/friendships', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ friend_id: friendId }),
      });

      if (response.ok) {
        // Refresh search to update button state
        searchUsers(searchQuery);
        // Also reload sent requests so it appears in pending tab
        loadSentRequests();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Erro ao enviar pedido de amizade');
      }
    } catch (error) {
      console.error('[FriendsModal] Error sending friend request:', error);
      toast.error('Erro ao enviar pedido de amizade');
    }
  };

  // Accept friend request
  const acceptRequest = async (friendshipId: string) => {
    if (!token) return;

    try {
      const response = await fetch(`https://mkey-api-production.fidelismadboat.workers.dev/friendships/${friendshipId}/accept`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        // Reload both pending and friends lists
        loadPendingRequests();
        loadSentRequests();
        loadFriends();
      }
    } catch (error) {
      console.error('[FriendsModal] Error accepting request:', error);
    }
  };

  // Reject friend request
  const rejectRequest = async (friendshipId: string) => {
    if (!token) return;

    try {
      const response = await fetch(`https://mkey-api-production.fidelismadboat.workers.dev/friendships/${friendshipId}/reject`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        loadPendingRequests();
      }
    } catch (error) {
      console.error('[FriendsModal] Error rejecting request:', error);
    }
  };

  // Cancel sent request
  const cancelRequest = async (friendshipId: string) => {
    if (!token) return;

    try {
      const response = await fetch(`https://mkey-api-production.fidelismadboat.workers.dev/friendships/${friendshipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        loadSentRequests();
      }
    } catch (error) {
      console.error('[FriendsModal] Error canceling request:', error);
    }
  };

  // Remove friend
  const removeFriend = async (friendshipId: string) => {
    if (!token) return;
    if (!confirm('Tem certeza que deseja remover este amigo?')) return;

    try {
      const response = await fetch(`https://mkey-api-production.fidelismadboat.workers.dev/friendships/${friendshipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        loadFriends();
      }
    } catch (error) {
      console.error('[FriendsModal] Error removing friend:', error);
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (!isOpen) return;

    if (activeTab === 'friends') {
      loadFriends();
    } else if (activeTab === 'pending') {
      loadPendingRequests();
      loadSentRequests();
    }
  }, [activeTab, isOpen, token]);

  // Search debounce
  useEffect(() => {
    if (activeTab !== 'add') return;

    const timer = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, activeTab, token]);

  if (!isOpen) return null;

  return createPortal(
    <div className="friends-modal-overlay" onClick={onClose}>
      <div className="friends-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="friends-modal-header">
          <h2>Amigos</h2>
          <button className="close-button" onClick={onClose}>
            <HiXMark size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="friends-tabs">
          <button
            className={`friends-tab ${activeTab === 'friends' ? 'active' : ''}`}
            onClick={() => setActiveTab('friends')}
          >
            <HiUsers size={20} />
            <span>Amigos</span>
            <span className="tab-badge">{friends.length}</span>
          </button>

          <button
            className={`friends-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <HiBell size={20} />
            <span>Pendentes</span>
            {(pendingRequests.length > 0 || sentRequests.length > 0) && (
              <span className="tab-badge pending">{pendingRequests.length + sentRequests.length}</span>
            )}
          </button>

          <button
            className={`friends-tab ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => setActiveTab('add')}
          >
            <HiUserPlus size={20} />
            <span>Adicionar</span>
          </button>

          <button
            className={`friends-tab ${activeTab === 'clans' ? 'active' : ''}`}
            onClick={() => setActiveTab('clans')}
          >
            <HiShieldCheck size={20} />
            <span>Clãs</span>
          </button>
        </div>

        {/* Content */}
        <div className="friends-content">
          {/* Friends List */}
          {activeTab === 'friends' && (
            <div className="friends-list">
              {friends.length === 0 ? (
                <div className="empty-state">
                  <HiUsers size={48} />
                  <p>Você ainda não tem amigos</p>
                  <button
                    className="btn-primary"
                    onClick={() => setActiveTab('add')}
                  >
                    Adicionar Amigos
                  </button>
                </div>
              ) : (
                friends.map(friendship => {
                  const displayName = friendship.friend?.display_name || friendship.friend?.username || 'User';
                  const username = friendship.friend?.username || 'unknown';
                  const title = friendship.friend?.odyssey_title || 'Adventurer';
                  const level = friendship.friend?.odyssey_level || 1;
                  const isOnline = friendship.friend?.is_online || false;

                  return (
                    <div key={friendship.id} className="friend-item">
                      <div className="friend-avatar">
                        {displayName[0]?.toUpperCase() || 'U'}
                        {isOnline && <div className="online-indicator" />}
                      </div>
                      <div className="friend-info">
                        <div className="friend-name">{displayName}</div>
                        <div className="friend-username">@{username}</div>
                        <div className="friend-title">
                          {title} • Nível {level}
                        </div>
                      </div>
                      <button
                        className="btn-remove"
                        onClick={() => removeFriend(friendship.id)}
                        title="Remover amigo"
                      >
                        <HiTrash size={18} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Pending Requests */}
          {activeTab === 'pending' && (
            <div className="pending-list">
              {pendingRequests.length === 0 && sentRequests.length === 0 ? (
                <div className="empty-state">
                  <HiBell size={48} />
                  <p>Nenhum pedido pendente</p>
                  <p className="empty-state-hint">Quando alguem enviar um pedido de amizade, ele aparecera aqui.</p>
                </div>
              ) : (
                <>
                  {/* Received Requests - Need your response */}
                  {pendingRequests.length > 0 && (
                    <>
                      <div className="pending-section-title">Pedidos Recebidos</div>
                      {pendingRequests.map(request => {
                        const displayName = request.friend?.display_name || request.friend?.username || 'User';
                        const username = request.friend?.username || 'unknown';
                        const title = request.friend?.odyssey_title || 'Adventurer';

                        return (
                          <div key={request.id} className="pending-item">
                            <div className="friend-avatar">
                              {displayName[0]?.toUpperCase() || 'U'}
                            </div>
                            <div className="friend-info">
                              <div className="friend-name">{displayName}</div>
                              <div className="friend-username">@{username}</div>
                              <div className="friend-title">{title}</div>
                            </div>
                            <div className="pending-actions">
                              <button
                                className="btn-accept"
                                onClick={() => acceptRequest(request.id)}
                                title="Aceitar"
                              >
                                <HiCheck size={20} />
                              </button>
                              <button
                                className="btn-reject"
                                onClick={() => rejectRequest(request.id)}
                                title="Rejeitar"
                              >
                                <HiXCircle size={20} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Sent Requests - Waiting for response */}
                  {sentRequests.length > 0 && (
                    <>
                      <div className="pending-section-title">Pedidos Enviados</div>
                      {sentRequests.map(request => {
                        const displayName = request.friend?.display_name || request.friend?.username || 'User';
                        const username = request.friend?.username || 'unknown';
                        const title = request.friend?.odyssey_title || 'Adventurer';

                        return (
                          <div key={request.id} className="pending-item sent">
                            <div className="friend-avatar">
                              {displayName[0]?.toUpperCase() || 'U'}
                            </div>
                            <div className="friend-info">
                              <div className="friend-name">{displayName}</div>
                              <div className="friend-username">@{username}</div>
                              <div className="friend-title">{title} • Aguardando resposta...</div>
                            </div>
                            <button
                              className="btn-cancel"
                              onClick={() => cancelRequest(request.id)}
                              title="Cancelar pedido"
                            >
                              <HiXCircle size={18} />
                            </button>
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Add Friends */}
          {activeTab === 'add' && (
            <div className="add-friends">
              <div className="search-box">
                <HiMagnifyingGlass size={20} className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar usuários..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="search-results">
                {loading ? (
                  <div className="loading-state">Buscando...</div>
                ) : searchQuery.length < 2 ? (
                  <div className="empty-state">
                    <HiMagnifyingGlass size={48} />
                    <p>Digite pelo menos 2 caracteres para buscar</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="empty-state">
                    <HiMagnifyingGlass size={48} />
                    <p>Nenhum usuario encontrado</p>
                    <p className="empty-state-hint">Verifique o nome ou tente buscar pelo email.</p>
                  </div>
                ) : (
                  searchResults.map(user => (
                    <div key={user.id} className="search-item">
                      <div className="friend-avatar">
                        {user.display_name[0].toUpperCase()}
                      </div>
                      <div className="friend-info">
                        <div className="friend-name">{user.display_name}</div>
                        <div className="friend-username">@{user.username}</div>
                        <div className="friend-title">
                          {user.odyssey_title} • Nível {user.odyssey_level}
                        </div>
                      </div>
                      <button
                        className="btn-add"
                        onClick={() => sendFriendRequest(user.id)}
                      >
                        <HiUserPlus size={18} />
                        Adicionar
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Clans Tab */}
          {activeTab === 'clans' && (
            <ClansPanel
              userTitle={useAuthStore.getState().user?.odyssey_title || 'Tripulante'}
              userLevel={useAuthStore.getState().user?.odyssey_level || 1}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
